<template>
  <b-card no-body>
    <b-overlay
      :show="loading"
      rounded="sm"
    >
      <b-table
        :items="users"
        :fields="fields"
        responsive
      >
        <template #cell(role_name)="data">
          <b-badge :variant="data.value | roleColor">
            {{ data.value | roleName }}
          </b-badge>
        </template>

        <template #cell(active_status)="data">
          <b-badge :variant="data.value ? 'success' : 'danger'">
            {{ data.value ? $t('active') : $t('unactive') }}
          </b-badge>
        </template>

        <template #cell(scope)="data">
          <div v-if="data.item.scoped_roles.length">
            <span
              v-for="scope in data.item.scoped_roles"
              :key="scope.role_resource_id"
              class="mr-1"
            >
              {{ scope.role_resource_name }}
            </span>
          </div>
        </template>

        <template #cell(phone)="data">
          {{ data.item.phone || '-' }}
        </template>

        <template #cell(created_at)="data">
          {{ data.value | date }}
        </template>

        <template #cell(tools)="data">
          <b-dropdown
            id="dropdown-1"
            size="sm"
            dropright
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="18"
                color="#000"
              />
            </template>
            <b-dropdown-item
              v-b-modal="`edit-user-${data.item.id}`"
              @click="selectedUser = { ...data.item }"
            >
              {{ $t('edit') }}
            </b-dropdown-item>
            <b-modal
              :id="`edit-user-${data.item.id}`"
              title="Editar usuario"
              @ok="handleEditUser()"
            >
              <b-form>
                <b-form-group label="Nombre">
                  <b-form-input v-model="selectedUser.name" />
                </b-form-group>
              </b-form>
              <b-form-group label="Teléfono">
                <b-form-input
                  v-model="selectedUser.phone"
                  type="tel"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="10"
                  :formatter="formatPhoneInput"
                  @input="selectedUser.phone = sanitizePhone($event)"
                  @keydown.native="restrictPhoneKeydown"
                />
              </b-form-group>
            </b-modal>

            <!--<b-dropdown-item
            :to="{ name: 'editUser', params: { id: data.item.id } }"
          >{{ $t('edit') }}</b-dropdown-item>-->
            <b-dropdown-item @click="deactivate(data.item)">
              <span
                :class="data.item.active_status ? 'text-danger' : 'text-success'"
              >
                {{
                  data.item.active_status ? $t('deactivate') : $t('reactivate')
                }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isAdmin"
              @click="removeUser(data.item)"
            >
              <span class="text-danger">Eliminar</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-overlay>
    <b-pagination
      class="mt-1 ml-1"
      :value="pagination.page"
      :total-rows="pagination.total_objects"
      :per-page="pagination.per_page"
      @change="handlePageChange($event)"
    />
  </b-card>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => [],
    },
    customerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedUser: {},
      loading: false,
      fields: [
        {
          key: 'name',
          label: 'Nombre',
        },
        {
          key: 'email',
          label: 'Email',
        },
        {
          key: 'phone',
          label: 'Teléfono',
        },
        {
          key: 'created_at',
          label: 'fecha de alta',
        },
        {
          key: 'active_status',
          label: this.$t('status'),
        },
        {
          key: 'tools',
          label: '',
        },
      ],
    }
  },
  computed: {
    // ...mapGetters('users', ['users']),
    isAdmin() {
      return this.$store.getters['auth/currentUser']?.role_name === 'admin'
    },
  },

  methods: {
    ...mapMutations('users', {
      changePage: 'CHANGE_PAGE',
    }),
    ...mapActions('users', ['fetchUsers', 'editUser', 'deactivateUser', 'deleteUser']),
    sanitizePhone(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 10)
    },
    formatPhoneInput(value) {
      return this.sanitizePhone(value)
    },
    restrictPhoneKeydown(event) {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ]

      if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return
      if (!/^\d$/.test(event.key)) event.preventDefault()
    },
    refreshMembers(page = this.pagination.page || 1) {
      return this.fetchUsers({
        by_customer: this.customerId,
        meta: {
          pagination: {
            page,
            per_page: this.pagination.per_page,
          },
        },
      })
    },
    deactivate(user) {
      this.deactivateUser(user).then(() => {
        this.refreshMembers()
      })
    },
    async removeUser(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${user.name} dejará de tener acceso y sus datos personales se borran. Su empresa y su historial no cambian.`,
        {
          title: '¿Eliminar este usuario?',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return
      try {
        await this.deleteUser(user.id)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Usuario eliminado', icon: 'CheckCircleIcon', variant: 'success' },
        })
        this.refreshMembers()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo eliminar',
            text: error?.response?.data?.messages?.[0] || 'Inténtalo de nuevo.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    handlePageChange(page) {
      this.loading = true
      this.changePage(page)
      this.refreshMembers(page).finally(() => {
        this.loading = false
      })
    },

    handleEditUser() {
      this.selectedUser.phone = this.sanitizePhone(this.selectedUser.phone)
      this.editUser({
        id: this.selectedUser.id,
        name: this.selectedUser.name,
        phone: this.selectedUser.phone,
      }).then(() => {
        this.refreshMembers().then(() => {
          this.loading = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
